import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";
import AuthFormCard from "../components/AuthFormCard";
import { getPasswordRules, isPasswordValid } from "../utils/passwordValidation";
import { signup, loginWithGoogle, loginWithGithub, sendVerificationEmail } from "../services/auth";
import "./AuthPage.css";

const PUBLIC_URL = process.env.PUBLIC_URL || "";
const EMAIL_VERIFICATION_BYPASS_IDS = new Set(
  (process.env.REACT_APP_EMAIL_VERIFICATION_BYPASS_IDS || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
);
const isBypassedUser = (uid?: string | null, email?: string | null): boolean =>
  EMAIL_VERIFICATION_BYPASS_IDS.has((uid || "").toLowerCase()) ||
  EMAIL_VERIFICATION_BYPASS_IDS.has((email || "").toLowerCase());

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [socialError, setSocialError] = useState("");

  const passwordRules = getPasswordRules(password);
  const showPasswordRules = passwordFieldFocused || password.length > 0;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthChecked(true);
      if (user && (user.emailVerified || isBypassedUser(user.uid, user.email))) {
        navigate("/", { replace: true });
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");
    setConfirmError("");
    setTermsError("");
    if (!email.trim()) {
      setEmailError("Please enter your email.");
      return;
    }
    if (!isPasswordValid(passwordRules)) {
      setPasswordError("Password does not meet all requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }
    if (!termsAgreed) {
      setTermsError("Please agree to the Terms of use and Privacy Policy.");
      return;
    }
    try {
      const { user } = await signup(email.trim(), password);
      try {
        await sendVerificationEmail(user);
      } catch {
        // Non-fatal: user can resend from the pending page
      }
      if (isBypassedUser(user.uid, user.email)) {
        navigate("/", { replace: true });
      } else {
        navigate("/verify-email-pending");
      }
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (code === "auth/email-already-in-use") {
        setEmailError("This email is already registered. Try signing in.");
      } else if (code === "auth/invalid-email") {
        setEmailError("Please enter a valid email address.");
      } else if (code === "auth/weak-password") {
        setPasswordError("Password is too weak. Please meet all requirements.");
      } else {
        setEmailError("Something went wrong. Please try again.");
      }
    }
  };

  const handleGoogle = async () => {
    setSocialError("");
    try {
      await loginWithGoogle();
      navigate("/");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      setSocialError(code === "auth/popup-closed-by-user" ? "" : "Sign up with Google failed. Please try again.");
    }
  };

  const handleGithub = async () => {
    setSocialError("");
    try {
      await loginWithGithub();
      navigate("/");
    } catch (err: unknown) {
      const code = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      setSocialError(code === "auth/popup-closed-by-user" ? "" : "Sign up with GitHub failed. Please try again.");
    }
  };

  if (!authChecked) return null;

  const canSubmit =
    !!email.trim() &&
    isPasswordValid(passwordRules) &&
    password === confirmPassword &&
    termsAgreed;

  const socialBlock = (
    <div className="auth-social">
      {socialError && <p className="auth-field-error" role="alert">{socialError}</p>}
      <button type="button" className="auth-social-btn" onClick={handleGoogle}>
        <img src={`${PUBLIC_URL}/Icon/Googlee.svg`} alt="" aria-hidden />
        Sign up with Google
      </button>
      <button type="button" className="auth-social-btn" onClick={handleGithub}>
        <img src={`${PUBLIC_URL}/image%201.svg`} alt="" aria-hidden />
        Sign up with GitHub
      </button>
    </div>
  );

  return (
    <AuthFormCard
      title="Sign up"
      subtitle="Welcome! Create your account."
      primaryButtonText="Sign up"
      primaryDisabled={!canSubmit}
      bottomText="Already have an account?"
      bottomLinkText="Sign In"
      bottomLinkTo="/login"
      onPrimarySubmit={handleSubmit}
      afterSubmit={socialBlock}
    >
      <div className="auth-field">
        <label htmlFor="signup-email">Email</label>
        <div className={`auth-input-wrapper${emailError ? " auth-input-wrapper--error" : ""}`}>
          <input
            id="signup-email"
            type="email"
            className="auth-input"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
          />
        </div>
        {emailError && <p className="auth-field-error" role="alert">{emailError}</p>}
      </div>
      <div className="auth-field">
        <label htmlFor="signup-password">Password</label>
        <div className={`auth-input-wrapper auth-input-wrapper--has-suffix${passwordError ? " auth-input-wrapper--error" : ""}`}>
          <input
            id="signup-password"
            type={showPassword ? "text" : "password"}
            className="auth-input"
            placeholder="Create a strong password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setPasswordFieldFocused(true)}
            onBlur={() => setPasswordFieldFocused(false)}
          />
          <span className="auth-input-suffix">
            <button
              type="button"
              className="auth-input-icon-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <img
                src={`${PUBLIC_URL}/${showPassword ? "input-suffix.svg" : "Vector.svg"}`}
                alt=""
              />
            </button>
          </span>
        </div>
        {showPasswordRules && (
        <div className="auth-password-rules" aria-live="polite">
          <span
            className={`auth-password-rule auth-password-rule--${passwordRules.lowercase ? "met" : "unmet"}`}
          >
            <span className="auth-password-rule-icon">
              {passwordRules.lowercase ? "✓" : "✗"}
            </span>
            Lowercase
          </span>
          <span
            className={`auth-password-rule auth-password-rule--${passwordRules.uppercase ? "met" : "unmet"}`}
          >
            <span className="auth-password-rule-icon">
              {passwordRules.uppercase ? "✓" : "✗"}
            </span>
            Uppercase
          </span>
          <span
            className={`auth-password-rule auth-password-rule--${passwordRules.number ? "met" : "unmet"}`}
          >
            <span className="auth-password-rule-icon">
              {passwordRules.number ? "✓" : "✗"}
            </span>
            Number
          </span>
          <span
            className={`auth-password-rule auth-password-rule--${passwordRules.symbol ? "met" : "unmet"}`}
          >
            <span className="auth-password-rule-icon">
              {passwordRules.symbol ? "✓" : "✗"}
            </span>
            Symbol
          </span>
          <span
            className={`auth-password-rule auth-password-rule--${passwordRules.minLength ? "met" : "unmet"}`}
          >
            <span className="auth-password-rule-icon">
              {passwordRules.minLength ? "✓" : "✗"}
            </span>
            10 character minimum
          </span>
        </div>
        )}
        {passwordError && <p className="auth-field-error" role="alert">{passwordError}</p>}
      </div>
      <div className="auth-field">
        <label htmlFor="signup-confirm">Confirm Password</label>
        <div className={`auth-input-wrapper auth-input-wrapper--has-suffix${confirmError ? " auth-input-wrapper--error" : ""}`}>
          <input
            id="signup-confirm"
            type={showConfirmPassword ? "text" : "password"}
            className="auth-input"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setConfirmError(""); }}
          />
          <span className="auth-input-suffix">
            <button
              type="button"
              className="auth-input-icon-btn"
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              <img
                src={`${PUBLIC_URL}/${showConfirmPassword ? "input-suffix.svg" : "Vector.svg"}`}
                alt=""
              />
            </button>
          </span>
        </div>
        {confirmError && <p className="auth-field-error" role="alert">{confirmError}</p>}
      </div>
      <label className="auth-terms">
        <input
          type="checkbox"
          checked={termsAgreed}
          onChange={(e) => setTermsAgreed(e.target.checked)}
        />
        <span>
          I agree to the{" "}
          <a href="https://trust-forge.ai/privacy_policy" target="_blank" rel="noopener noreferrer" className="auth-link">
            Terms of use and Privacy Policy
          </a>
        </span>
        {termsError && <p className="auth-field-error" role="alert">{termsError}</p>}
      </label>
    </AuthFormCard>
  );
};

export default SignupPage;
